import { useCallback, useEffect } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import { requestNotificationPermission } from 'app/utils/firebaseUtils';
import { notify, playNotificationSound } from 'app/utils/notification/notification';
import { firebaseConfigQuery } from 'graphql/app/pushNotificationQueries';
import { initializeApp } from 'firebase/app';
import { getMessaging, onMessage } from 'firebase/messaging';
import { setPushNotificationStatus } from 'store/actions/app/appActions';
import { useDispatch } from 'react-redux';
import { sendNotificationSubscription } from './Notification';
import { cut } from 'app/utils/string/string-utils';

const pushChannel = new BroadcastChannel('push-notification');

const PushNotifications = () => {
    const dispatch = useDispatch();
    const { data } = useQuery(firebaseConfigQuery);
    // TODO: Remove the notification implementation after testing and if we want to keep the push notifications implementation
    const { data: notification } = useSubscription(sendNotificationSubscription);

    useEffect(() => {
        const message = notification?.onSendMessage || '';
        if (message) {
            notify('Notification Received', {
                body: cut(message, 30),
            });
        }
    }, [notification]);

    pushChannel.onmessage = event => {
        if (event?.data?.type === 'playSound') {
            playNotificationSound();
        }
    };

    const intiFirebaseApp = useCallback(async config => {
        const { vapidKey, ...appConfig } = config;
        const app = initializeApp(appConfig); // Initialize Firebase
        const messaging = getMessaging(app); // Initialize Firebase Cloud Messaging and get a reference to the service
        const permission = await requestNotificationPermission(messaging, vapidKey);
        if (permission === 'granted') {
            dispatch(setPushNotificationStatus(true));
            onMessage(messaging, payload => {
                const { topic, payload: messageStr } = payload?.data || {};
                // no need to show the notification when user login and subscribe to notifications
                if (topic === 'subscription') return Promise.resolve();
                let message = {};
                try {
                    message = JSON.parse(messageStr || '{}');
                } catch (err) {
                    console.error('Failed to parse payload message:', messageStr, err);
                }
                const taskId = message?.id;
                const link = taskId ? `/#/abox/task/${taskId}` : undefined;
                // this change should also reflected in sw.js file to handle background message
                const notificationTitle = topic?.toLowerCase() === 'assigned task' ? 'New Task Assigned' : (topic || 'Notification')
                const isPWA = window?.navigator?.standalone || window.matchMedia('(display-mode: standalone)').matches;
                notify(notificationTitle, {
                    body: message?.name || 'You have a new message',
                    icon: '/images/affectliCircle.png',
                    data: {
                        link,
                        isNewWindow: isPWA ? false : true,
                    }
                });
            });
        } else {
            dispatch(setPushNotificationStatus(false));
        }
    }, [dispatch]);

    useEffect(() => {
        if (data) {
            const config = data?.affectliSettings?.messaging;
            if (config) {
                intiFirebaseApp(config);
            } else {
                console.log('Push-notification: firebase configurations not found'); // eslint-disable-line
                dispatch(setPushNotificationStatus(false));
            }
        }
    }, [data, intiFirebaseApp, dispatch]);

    return null;
};

export default PushNotifications;
